<template>
  <a-select
    v-model:value="innerValue"
    :options="selectOptions"
    :placeholder="placeholder"
    :disabled="disabled || !selectOptions.length"
    :size="size"
    :allow-clear="allowClear"
    :show-search="showSearch"
    :filter-option="filterAreaOption"
    :loading="loading"
    :status="status"
    :not-found-content="notFoundContent"
    class="ant-district-select"
    @change="onChange"
  />
</template>

<script lang="ts" setup>
import { computed, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import {
  filterAreaOption,
  mapAreaOptions,
  findAreaById,
  type AreaNode,
  type AreaSelectOption,
} from "./useArea";

interface Props {
  modelValue?: string | number | null;
  /** Danh sách quận/huyện (thường lấy từ children của tỉnh) */
  options?: AreaNode[];
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  showSearch?: boolean;
  loading?: boolean;
  status?: "" | "error" | "warning";
  notFoundContent?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  options: () => [],
  placeholder: "Chọn Quận / Huyện",
  disabled: false,
  size: "middle",
  allowClear: true,
  showSearch: true,
  loading: false,
  status: "",
  notFoundContent: "Không có dữ liệu",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
  (
    e: "change",
    value: string | number | null,
    option: AreaSelectOption | null,
    wards: AreaNode[]
  ): void;
}>();

const selectOptions = computed(() => mapAreaOptions(props.options));

const innerValue = computed({
  get: () => props.modelValue,
  set: (val: string | number | null) => emit("update:modelValue", val),
});

watch(
  () => props.options,
  () => {
    if (
      props.modelValue != null &&
      !findAreaById(props.options, props.modelValue)
    ) {
      emit("update:modelValue", null);
      emit("change", null, null, []);
    }
  }
);

const onChange = (val: string | number | null, option: any) => {
  const selected = (option as AreaSelectOption) || null;
  const node = selected?.raw || findAreaById(props.options, val);
  emit("update:modelValue", val);
  emit("change", val, selected, node?.children || []);
};
</script>

<style scoped>
.ant-district-select {
  width: 100%;
}
</style>

<!--
  Hướng dẫn sử dụng — District Select
  Global: <h-district v-model="district" :options="districts" @change="onDistrict" />
  Import: import { DistrictSelect } from '@/components/ant/address'

  @change(value, option, wards)
  :options lấy từ children của tỉnh
  Docs: src/components/ant/address/README.md
-->
